/**
 * ForgotPassword.jsx
 *
 * Handles the full forgot password flow in three steps:
 *   Step 1 - User enters their email to request a reset link
 *   Step 'sent' - Confirmation screen shown after email is sent
 *   Step 2 - User sets a new password after clicking the link from email
 *
 * Cognito API calls:
 *   forgotPassword()      - triggers the reset code email (step 1)
 *   confirmPassword()     - validates the code and updates the password (step 2)
 *
 * The reset code and email are delivered via email as URL params (?code=...&email=...).
 * The user's email is preserved in localStorage between step 1 and step 2
 * since Cognito does not support {username} as a URL placeholder in email templates.
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { userPool as userpool } from '../constants/constants';
import { COLORS, FONT_FAMILY } from '../talkTreeShared/Tree/constants/theme';
import { Typography } from '@mui/material';

export default function ForgotPassword() {
  const navigate = useNavigate();

  // step: 1 = email form | 'sent' = check your email | 2 = set new password
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [codeFromUrl, setCodeFromUrl] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [pwMin8, setPwMin8] = useState(false);
  const [pwLC1, setPwLC1] = useState(false);
  const [pwUC1, setPwUC1] = useState(false);
  const [pwNum1, setPwNum1] = useState(false);
  const [pwSC1, setPwSC1] = useState(false);
  const [noSpaces, setNoSpaces] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = 'mulish-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // If the user arrives via the email link (?code=...), jump to step 2.
  // Email is retrieved from localStorage (saved when user submitted step 1).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    const urlEmail = params.get('email');
    if (urlCode && urlEmail) {
      setCodeFromUrl(urlCode);
      setEmail(urlEmail);
      setStep(2);
      mixpanel.track('forgotPasswordResetLinkClicked', { email: urlEmail });
    } else {
      mixpanel.track('forgotPasswordInitialView');
    }
  }, []);

  const pwChecks = [
    { label: '8 Characters Minimum', ok: pwMin8 },
    { label: '1 lower case letter', ok: pwLC1 },
    { label: '1 upper case letter', ok: pwUC1 },
    { label: '1 number', ok: pwNum1 },
    { label: '1 special character', ok: pwSC1 },
  ];

  const passwordValid = pwMin8 && pwLC1 && pwUC1 && pwNum1 && pwSC1 && !noSpaces;
  const confirmMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = passwordValid && confirmMatch;

  const handlePasswordChange = (e) => {
    const v = e.target.value;
    setNewPassword(v);
    setNoSpaces(v.includes(' '));
    setPwMin8(v.length >= 8);
    setPwLC1(/[a-z]/.test(v));
    setPwUC1(/[A-Z]/.test(v));
    setPwNum1(/\d/.test(v));
    setPwSC1(/[!@#$%^&*(),.?":{}|<>]/.test(v));
  };

  // Step 1: validate email, save it to Local Storage, then trigger Cognito reset email
  const handleSendCode = (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email address.'); return; }
    // Basic format check: requires something before @, a domain, and a TLD — without being overly strict
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      mixpanel.track('forgotPasswordInvalidEmail', { email: email });
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);

    const cognitoUser = new CognitoUser({ Username: email, Pool: userpool });
    cognitoUser.forgotPassword({
      onSuccess: () => {
        setLoading(false);
        mixpanel.track('forgotPasswordResetRequestedViaEmail', { email: email });
        setStep('sent'); // show "check your email" screen — user must click link before reaching step 2
      },
      onFailure: (err) => {
        setLoading(false);
        mixpanel.track('forgotPasswordResetRequestFailed', { email: email, error: err.code || err.message || 'Unknown error' });
        if (err.code === 'UserNotFoundException') setError('No account found with this email.');
        else if (err.code === 'LimitExceededException') setError('Too many attempts. Please try again later.');
        else setError(err.message || 'Failed to send reset email. Please try again.');
      },
    });
  };

  // Step 2: confirm new password using the code from the URL and email
  const handleResetPassword = (e) => {
    e.preventDefault();
    setError('');
    if (!codeFromUrl) {
      setError('Reset code not found. Please use the link from your email.');
      return;
    }
    setLoading(true);

    const cognitoUser = new CognitoUser({ Username: email, Pool: userpool });
    cognitoUser.confirmPassword(codeFromUrl, newPassword, {
      onSuccess: () => {
        setLoading(false);
        mixpanel.track('forgotPasswordNewPasswordSuccess', { email: email });
        // Navigate to login with flag so Login can show a success banner
        navigate('/login?passwordReset=true');
      },
      onFailure: (err) => {
        setLoading(false);
        mixpanel.track('forgotPasswordNewPasswordFail', { email: email });
        if (err.code === 'CodeMismatchException') setError('Invalid reset link. Please request a new one.');
        else if (err.code === 'ExpiredCodeException') setError('Reset link has expired. Please request a new one.');
        else setError(err.message || 'Password reset failed. Please try again.');
      },
    });
  };

  const resetToStep1 = () => {
    mixpanel.track('forgotPasswordTryAgainClicked', { email: email });
    setStep(1);
    setError('');
    setCodeFromUrl('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .tt-left-panel { display: none !important; }
          .tt-right-panel { padding: 40px 20px !important; align-items: flex-start !important; }
          .tt-form-box { max-width: 100% !important; }
          .tt-title { font-size: 26px !important; margin: 32px 0 28px !important; }
        }
      `}</style>

      <div role="main" style={{ display: 'flex', minHeight: '100vh',    overflow: 'hidden' }}>

        {/* Left panel — flowering tree on soft cream gradient */}
        <div
          className="tt-left-panel"
          aria-hidden="true"
          style={{
            flex: '0 0 46%',
            background: COLORS.panelGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'sticky', top: 0, height: '100vh',
          }}
        >
          <img
            src="/SampleTree.png"
            alt=""
            role="presentation"
            style={{ width: '62%', maxWidth: '420px', objectFit: 'contain' }}
          />
        </div>

        {/* Right panel — form */}
        <div className="tt-right-panel" style={{
          flex: 1, backgroundColor: COLORS.page,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '40px 24px', overflowY: 'auto',
        }}>
          <div className="tt-form-box" style={{ width: '100%', maxWidth: '420px' }}>

            {/* Live region for dynamic error messages */}
            <div aria-live="polite" aria-atomic="true">
              {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px',   }}>{error}</Alert>}
            </div>

            {/* Step 1: Email form */}
            {step === 1 && (
              <>
                <Typography
                  component="h1"
                  sx={{
                      
                    fontWeight: 500,
                    fontSize: { xs: 26, sm: 32 },
                    letterSpacing: '-0.5px',
                    color: COLORS.primary,
                    lineHeight: 1.1,
                  }}
                >
                  Trouble accessing your account?
                </Typography>
                <p style={subtitleStyle}>
                  Enter your email address and we'll send you a password reset link.
                </p>

                <form onSubmit={handleSendCode} noValidate aria-label="Request password reset" aria-busy={loading}>
                  <TextField
                    required fullWidth autoFocus
                    id="email" label="Email address" name="email"
                    autoComplete="email"
                    inputProps={{ 'aria-required': true }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={fieldSx}
                  />
                  <Button type="submit" fullWidth variant="contained" disableElevation disabled={loading} sx={buttonSx}>
                    {loading ? 'Sending...' : 'Send me a password reset link'}
                  </Button>
                  <div style={{ textAlign: 'center', marginTop: '24px' }}>
                    <span style={{ fontSize: '15px', color: COLORS.text }}>Don&apos;t have an account? </span>
                    <RouterLink to="/signup" style={linkStyle} aria-label="Go to sign up page">
                      Sign up
                    </RouterLink>
                  </div>
                </form>
              </>
            )}

            {/* Step 'sent': Check your email */}
            {step === 'sent' && (
              <div aria-live="polite">
                <Typography
                                  component="h1"
                                  sx={{
                                      
                                    fontWeight: 500,
                                    fontSize: { xs: 26, sm: 32 },
                                    letterSpacing: '-0.5px',
                                    color: COLORS.primary,
                                    lineHeight: 1.1,
                                  }}
                                >Check your email</Typography>
                <p style={{ textAlign: 'center', fontSize: '15px', color: COLORS.text, lineHeight: 1.7, marginBottom: '8px' }}>
                  We sent a password reset link to
                </p>
                <p style={{ textAlign: 'center', fontSize: '15px', color: COLORS.primary, fontWeight: 700, marginBottom: '24px' }}>
                  {email}
                </p>
                <p style={{ textAlign: 'center', fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6, marginBottom: '32px' }}>
                  Click the link in that email to set a new password. The link expires in 1 hour.
                </p>
                <p style={{ textAlign: 'center', fontSize: '13px', color: COLORS.textMuted }}>
                  Didn't receive it?{' '}
                  <button
                    onClick={resetToStep1}
                    aria-label="Try sending the reset link again"
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: COLORS.primary, fontSize: '13px', fontWeight: 600,
                      cursor: 'pointer',   
                      textDecoration: 'underline',
                    }}
                  >
                    Try again
                  </button>
                </p>
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <RouterLink to="/login" style={linkStyle} aria-label="Back to log in page">
                    Back to log in
                  </RouterLink>
                </div>
              </div>
            )}

            {/* Step 2: Set new password */}
            {step === 2 && (
              <>
                <h1 className="tt-title" style={titleStyle}>Set a new password</h1>

                <form
                  onSubmit={handleResetPassword}
                  noValidate
                  aria-label="Set new password form"
                  aria-busy={loading}
                >
                  <TextField
                    required fullWidth autoFocus
                    id="newPassword" label="Password"
                    type="password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    error={noSpaces}
                    helperText={noSpaces ? 'Password cannot contain spaces' : ''}
                    inputProps={{
                      'aria-required': true,
                      'aria-invalid': noSpaces,
                      'aria-describedby': 'password-requirements',
                    }}
                    sx={{ ...fieldSx, mb: 2 }}
                  />

                  <TextField
                    required fullWidth
                    id="confirmPassword" label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={confirmMismatch}
                    helperText={confirmMismatch ? "Passwords don't match" : ''}
                    inputProps={{
                      'aria-required': true,
                      'aria-invalid': confirmMismatch,
                    }}
                    sx={{
                      ...fieldSx, mb: 2,
                      ...(confirmMismatch ? errorFieldSx : {}),
                      ...(confirmMatch ? successFieldSx : {}),
                    }}
                  />

                  {/* Password requirements checklist */}
                  <div
                    id="password-requirements"
                    role="status"
                    aria-label="Password requirements"
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', justifyContent: 'center', margin: '4px 0 20px' }}
                  >
                    {pwChecks.map(({ label, ok }) => (
                      <label
                        key={label}
                        aria-label={`${label}: ${ok ? 'met' : 'not met'}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: COLORS.text, cursor: 'default',   }}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                            border: `2px solid ${ok ? COLORS.success : '#9ca3af'}`,
                            backgroundColor: ok ? COLORS.success : '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {ok && (
                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
                              <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </span>
                        {label}
                      </label>
                    ))}
                  </div>

                  <Button
                    type="submit" fullWidth variant="contained" disableElevation
                    disabled={loading || !canSubmit}
                    sx={{
                      ...buttonSx,
                      backgroundColor: canSubmit ? COLORS.primary : COLORS.disabled,
                      '&:hover': { backgroundColor: canSubmit ? COLORS.primaryHover : COLORS.disabled, boxShadow: 'none' },
                      '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#fff' },
                    }}
                  >
                    {loading ? 'Resetting...' : 'Submit password change'}
                  </Button>
                </form>
              </>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

const titleStyle = {
    
  fontSize: '34px', fontWeight: 800, color: COLORS.primary,
  letterSpacing: '-0.5px', lineHeight: 1.15,
  textAlign: 'center', margin: '0 0 20px',
};

const subtitleStyle = {
  textAlign: 'center', fontSize: '15px', color: COLORS.text,
  marginBottom: '32px', lineHeight: 1.6,   
};

const linkStyle = { fontSize: '15px', color: COLORS.primary, fontWeight: 600, textDecoration: 'underline' };

const buttonSx = {
  mt: 3.5,
  mb: 3,
  backgroundColor: COLORS.primary,
  borderRadius: '4px',
  py: 1.1,
  fontWeight: 700,
  fontSize: 15,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { backgroundColor: COLORS.primaryHover, boxShadow: 'none' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
      
    '&.Mui-focused fieldset': { borderColor: COLORS.primary },
  },
  '& .MuiInputLabel-root': {   },
  '& .MuiFormHelperText-root': {   },
  '& label.Mui-focused': { color: COLORS.primary },
};

const errorFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: COLORS.error },
    '&:hover fieldset': { borderColor: COLORS.error },
    '&.Mui-focused fieldset': { borderColor: COLORS.error },
  },
  '& label': { color: COLORS.error },
  '& label.Mui-focused': { color: COLORS.error },
  '& .MuiFormHelperText-root': { color: COLORS.error },
};

const successFieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: COLORS.success },
    '&:hover fieldset': { borderColor: COLORS.success },
    '&.Mui-focused fieldset': { borderColor: COLORS.success },
  },
};